import { useState, useEffect, useCallback, useRef } from 'react';
import { useCRM } from '../context/CRMContext';

const SYNC_POLL_MS = 45000;
// Export walks the same paginated endpoint in big chunks. Measured on a 93k
// lead DB: 1000/page costs ~2.2ms per row, 2000/page ~1.45ms, and 5000/page is
// no faster than 2000 while making each request block ~7s (so progress stalls
// and the payload spike gets worse). 2000 is the sweet spot.
const EXPORT_PAGE_SIZE = 2000;
// Backstop so a wrong `total` from the server can never spin forever.
const EXPORT_MAX_PAGES = 1000;

// Translate the UI filter object into API query params. Shared by the paged
// list load and the export-all walk so both always apply identical filters.
const buildFilterParams = (f, baseFilters) => {
  const params = { ...baseFilters };
  const csv = (v) => (Array.isArray(v) ? v.filter(Boolean).join(',') : v);

  if (f.search) params.search = f.search;
  if (csv(f.status)) params.statuses = csv(f.status);
  if (csv(f.priority)) params.priorities = csv(f.priority);
  if (csv(f.source)) params.sources = csv(f.source);
  if (csv(f.service_type)) params.service_types = csv(f.service_type);
  if (csv(f.property_type)) params.property_types = csv(f.property_type);
  if (csv(f.project)) params.projects = csv(f.project);
  if (f.assigned) params.assign_status = f.assigned;
  if (csv(f.assigned_to)) params.assigned_to = csv(f.assigned_to);
  if (Array.isArray(f.form_name) ? f.form_name.filter(Boolean).length : f.form_name)
    params.form_name = Array.isArray(f.form_name) ? f.form_name.filter(Boolean).join('||') : f.form_name;
  if (f.date_from) params.created_from = f.date_from;
  if (f.date_to) params.created_to = f.date_to;
  if (f.followup_from) params.followup_from = f.followup_from;
  if (f.followup_to) params.followup_to = f.followup_to;
  if (f.followup_filter) params.followup_filter = f.followup_filter;
  if (f.lead_type === 'fresh') params.is_fresh = true;
  else if (f.lead_type === 'imported') params.is_imported = true;

  return params;
};

/**
 * Hook to fetch leads from API with filters and pagination.
 * @param {Object} baseFilters - Default filters to always apply (e.g., { is_fresh: true })
 */
const useLeads = (baseFilters = {}) => {
  const { fetchLeads } = useCRM();
  const [leads, setLeads] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '', status: '', priority: '', source: '',
    service_type: '', property_type: '', project: '', assigned: '', assigned_to: '',
    form_name: '', date_from: '', date_to: '',
  });

  const currentPage = useRef(1);
  const currentLimit = useRef(25);
  // Rapid filter/tab changes leave several fetches in flight at once. Responses
  // can resolve out of order, so an older one must never overwrite a newer one.
  const reqIdRef = useRef(0);

  const loadLeads = useCallback(async (overrideFilters, page, limit, { silent = false } = {}) => {
    // silent = background refresh (poll / tab focus): update data in place
    // without flashing the full-page "Loading leads..." spinner.
    const reqId = ++reqIdRef.current;
    if (!silent) setLoading(true);
    try {
      const f = overrideFilters || filters;
      const params = buildFilterParams(f, baseFilters);

      params.page = page || currentPage.current;
      params.limit = limit || currentLimit.current;

      const res = await fetchLeads(params);
      if (reqId !== reqIdRef.current) return; // superseded — discard this response
      if (res.success) {
        setLeads(res.data?.leads || []);
        setPagination(res.data?.pagination || {});
      }
    } catch (err) {
      console.error('useLeads error:', err);
    } finally {
      // Only the newest request may clear the spinner, otherwise an older
      // response ends the loading state while fresh data is still on the wire.
      // Not gated on `silent`: a background poll can supersede a visible load,
      // and it must still release the spinner that load turned on.
      if (reqId === reqIdRef.current) setLoading(false);
    }
  }, [fetchLeads, filters, baseFilters]);

  // Load on mount and when baseFilters change
  const baseKey = JSON.stringify(baseFilters);
  useEffect(() => {
    currentPage.current = 1;
    loadLeads(undefined, 1);
  }, [baseKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSearch = useCallback((newFilters) => {
    currentPage.current = 1;
    loadLeads(newFilters, 1);
  }, [loadLeads]);

  const onPageChange = useCallback((page, limit) => {
    currentPage.current = page;
    if (limit) currentLimit.current = limit;
    loadLeads(filters, page, limit);
  }, [loadLeads, filters]);

  const refresh = useCallback(() => {
    loadLeads(undefined, undefined, undefined, { silent: true });
  }, [loadLeads]);

  /**
   * Fetch EVERY lead matching the current filters, not just the visible page.
   * The list endpoint is paginated with no "all" mode, so walk it page by page.
   * Note: deliberately NOT sent with `light=1`. That flag drops the per-lead
   * comment aggregation, and the CSV exports a Comments column.
   * @param {(loaded: number, total: number) => void} [onProgress]
   * @returns {Promise<Array>} every matching lead
   */
  const exportAll = useCallback(async (onProgress) => {
    const params = {
      ...buildFilterParams(filters, baseFilters),
      limit: EXPORT_PAGE_SIZE,
    };

    const all = [];
    let total = null;

    for (let page = 1; page <= EXPORT_MAX_PAGES; page++) {
      const res = await fetchLeads({ ...params, page });
      if (!res.success) throw new Error(res.message || 'Failed to fetch leads for export');

      const batch = res.data?.leads || [];
      all.push(...batch);
      if (total === null) total = res.data?.pagination?.total ?? batch.length;

      if (onProgress) onProgress(all.length, total);

      // A short page means we've reached the end.
      if (batch.length < EXPORT_PAGE_SIZE || all.length >= total) break;
    }

    return all;
  }, [fetchLeads, filters, baseFilters]);

  // Keep a stable ref to the latest refresh so sync listeners don't re-subscribe on every filter change
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  // Cross-device sync: refetch on tab focus/visibility regain + poll every 45s while visible
  useEffect(() => {
    const doRefresh = () => refreshRef.current();
    const onFocus = () => doRefresh();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') doRefresh();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    const intervalId = setInterval(() => {
      if (document.hidden) return;
      doRefresh();
    }, SYNC_POLL_MS);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(intervalId);
    };
  }, []);

  return {
    leads,
    loading,
    pagination,
    filters,
    setFilters,
    onSearch,
    onPageChange,
    refresh,
    exportAll,
  };
};

export default useLeads;
